import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

// Same categories the help center searches (help/index.js HELP_SEARCH_CATEGORIES).
const HELP_SEARCH_CATEGORIES = ['help', 'news'];

function excerpt(text) {
    if (!text) {
        return '';
    }
    const plain = String(text)
        .replace(/[#*_~`>[\]()!|-]/g, '')
        .replace(/\n+/g, ' ')
        .trim();
    return plain.length > 160 ? `${plain.substring(0, 160)}...` : plain;
}

/**
 * Live help-center results for one logged search gap, so an admin can verify what a member
 * saw. Runs the exact same article query as the help center search (hostname, help + news
 * categories, stemmed search), lists the hits, and lets the admin open an article in the
 * editor or mark the gap as done right here.
 */
@observer
class SearchResultsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            articles: [],
            saving: false,
            status: props.row?.status || 'open',
        };
    }

    componentDidMount() {
        this.search();
    }

    search = async () => {
        const { row } = this.props;
        const { articleStore } = this.props.stores;
        const search = (row?.queryDisplay || row?.query || '').trim();
        if (!search) {
            return;
        }
        this.setState({ loading: true });
        const articles = await articleStore.load({
            query: {
                hostname: articleStore.currentHostname,
                category: HELP_SEARCH_CATEGORIES,
                includeMainArticles: 1,
                stemmedSearch: 1,
                search,
                all: 1,
            },
            skipUpdate: true,
        });
        this.setState({ loading: false, articles: Array.isArray(articles) ? articles : [] });
    }

    onClickArticle = (e) => {
        const articleId = parseInt(e.target.closest('[data-article]').dataset.article, 10);
        if (!(articleId > 0)) {
            return;
        }
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('editArticle', {
            height: drawerHeightLarge,
            id: articleId,
            callback: () => {
                appState.toggleDrawer(false, drawerLevel + 1);
                this.search();
            },
        }, drawerLevel + 1);
    }

    onClickSetStatus = async (e) => {
        const { status } = e.target.closest('button').dataset;
        const { row, callback } = this.props;
        const { helpSearchStore } = this.props.stores;
        if (!row?.id || !status) {
            return;
        }
        this.setState({ saving: true });
        const response = await helpSearchStore.setStatus(row.id, status);
        this.setState({ saving: false });
        if (response?.status === 202) {
            this.setState({ status });
            if (typeof callback === 'function') {
                callback();
            }
        }
    }

    render() {
        const { row = {} } = this.props;
        const {
            loading, articles, saving, status,
        } = this.state;
        const query = row.queryDisplay || row.query || '';

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <h5 class='ms-3 mb-1'>
                        <i class='fa-solid fa-magnifying-glass me-2' />
                        <Text id='help.search-results-title'>Help center results for</Text> «{query}»
                    </h5>
                    <p class='ms-3 mb-3 text-body-secondary' style='font-size: 0.9em;'>
                        <Text id='help.search-results-help'>This is the same search the member ran. Open an article to improve it, or mark the search as done if it is answered now.</Text>
                    </p>

                    <div class='ms-3 mb-3 d-flex flex-row flex-wrap align-items-center gap-2'>
                        <span class='badge rounded-pill text-bg-secondary'>
                            <Text id='help.search-results-zero-hits' fields={{ count: row.zeroHitCount || 0 }}>{row.zeroHitCount || 0} searches without hits</Text>
                        </span>
                        {!loading && (
                            <span class={`badge rounded-pill ${articles.length > 0 ? 'text-bg-success' : 'text-bg-danger'}`}>
                                <Text id='help.search-results-live-hits' fields={{ count: articles.length }}>{articles.length} hits now</Text>
                            </span>
                        )}
                        {loading && <span class='text-body-secondary'><i class='fa-solid fa-spinner fa-spin' /></span>}
                        <span class='flex-grow-1' />
                        {status !== 'done' && (
                            <button
                                type='button'
                                class='btn btn-sm btn-success rounded-pill'
                                data-status='done'
                                onClick={this.onClickSetStatus}
                                disabled={saving}
                            >
                                <i class='fa-solid fa-check' /> <Text id='help.search-gaps-done'>Done</Text>
                            </button>
                        )}
                        {status === 'done' && (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                data-status='open'
                                onClick={this.onClickSetStatus}
                                disabled={saving}
                            >
                                <i class='fa-solid fa-rotate-left' /> <Text id='help.search-gaps-reopen'>Reopen</Text>
                            </button>
                        )}
                    </div>

                    {!loading && articles.length === 0 && (
                        <div class='mx-3 p-4 text-center text-body-secondary bg-body-secondary rounded'>
                            <i class='fa-solid fa-circle-xmark me-1' />
                            <Text id='help.search-results-empty'>No articles match this search right now.</Text>
                        </div>
                    )}

                    {articles.length > 0 && (
                        <div class='list-group mx-3'>
                            {articles.map(article => (
                                <button
                                    key={`help-search-result-${article.id}`}
                                    type='button'
                                    class='list-group-item list-group-item-action'
                                    data-article={article.id}
                                    onClick={this.onClickArticle}
                                >
                                    <div class='d-flex justify-content-between align-items-start gap-2'>
                                        <strong class='text-break'>{article.title}</strong>
                                        <small class='text-body-secondary text-nowrap'>{article.category}</small>
                                    </div>
                                    <small class='text-body-secondary d-block text-break'>{excerpt(article.body)}</small>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default SearchResultsDrawer;
